import React, { useMemo, useState } from "react";
import { TrendingUp, Target, Filter, Send, Handshake, Wallet, Search } from "lucide-react";

/**
 * DashboardInsights — three visual upgrades for the Live Demo dashboard:
 *   1. EarningsChart  — 7d / 30d / 90d area chart of ₹ earned per day
 *   2. GoalRing       — monthly ₹ goal with progress ring + EOM projection
 *   3. FunnelWidget   — Scanned → Proposals → Deals → Payouts conversion
 *
 * All numbers are deterministic per agent so they stay stable between
 * renders within a session (no jitter on every re-render).
 */

const DAILY_BASE_HOURS = 5.4; // avg billable hours/day across a roster
const MONTHLY_GOAL_DEFAULT = 200000; // ₹2L default monthly target

/* -------- deterministic pseudo-random (mulberry32) -------- */
function rng(seedStr) {
  let h = 1779033703 ^ (seedStr?.length || 0);
  for (let i = 0; i < (seedStr || "").length; i++) {
    h = Math.imul(h ^ seedStr.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function () {
    h |= 0;
    h = (h + 0x6d2b79f5) | 0;
    let t = Math.imul(h ^ (h >>> 15), 1 | h);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function fmtINR(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

function shortINR(n) {
  if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return `₹${n}`;
}

/* -------- earnings series -------- */
function buildEarningsSeries(agent, days) {
  const rate = Number(agent?.hourly_rate) || 4500;
  const seed = (agent?.id || agent?.name || "default") + ":" + days;
  const rand = rng(seed);
  const today = new Date();
  const out = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const wk = d.getDay();
    const weekdayBoost = wk === 0 || wk === 6 ? 0.45 : 1; // weekend dip
    const variance = 0.7 + rand() * 0.9; // 0.7 — 1.6x
    const spike = rand() > 0.92 ? 1.8 + rand() : 1;
    const value = rate * DAILY_BASE_HOURS * weekdayBoost * variance * spike;
    out.push({
      date: d,
      value: Math.round(value),
    });
  }
  return out;
}

/* -------- chart -------- */
function EarningsChart({ agent }) {
  const [range, setRange] = useState(30);
  const series = useMemo(() => buildEarningsSeries(agent, range), [agent, range]);

  const total = series.reduce((s, p) => s + p.value, 0);
  const avg = total / series.length;
  const prev = useMemo(
    () => buildEarningsSeries(agent, range * 2).slice(0, range).reduce((s, p) => s + p.value, 0),
    [agent, range]
  );
  const delta = prev ? Math.round(((total - prev) / prev) * 100) : 0;

  // Build SVG path
  const W = 600;
  const H = 160;
  const PAD_T = 8;
  const PAD_B = 18;
  const max = Math.max(...series.map((p) => p.value), 1);
  const step = series.length > 1 ? W / (series.length - 1) : W;
  const points = series.map((p, i) => ({
    x: i * step,
    y: PAD_T + (H - PAD_T - PAD_B) * (1 - p.value / max),
    raw: p,
  }));
  const linePath = points.map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${W.toFixed(1)},${(H - PAD_B).toFixed(1)} L0,${(H - PAD_B).toFixed(1)} Z`;

  return (
    <div
      data-testid="dashboard-earnings-chart"
      className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Earnings · last {range}d
          </p>
          <p className="font-display text-3xl md:text-4xl font-black tracking-tighter text-slate-900 leading-none mt-2 tabular-nums">
            {fmtINR(total)}
          </p>
          <p
            className={`text-xs font-semibold mt-1 inline-flex items-center gap-1 ${
              delta >= 0 ? "text-emerald-600" : "text-red-600"
            }`}
          >
            <TrendingUp size={11} strokeWidth={2.5} />
            {delta >= 0 ? "+" : ""}{delta}% vs prior {range}d · avg {shortINR(avg)}/day
          </p>
        </div>
        <div
          role="tablist"
          data-testid="earnings-range-tabs"
          className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5"
        >
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              role="tab"
              data-testid={`earnings-range-${d}`}
              aria-selected={range === d}
              onClick={() => setRange(d)}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition ${
                range === d
                  ? "bg-white text-slate-900 shadow-[0_1px_3px_-1px_rgba(15,23,42,0.18)]"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 -mx-1">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-[170px]"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="earnGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgb(37 99 235)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="rgb(37 99 235)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* Light horizontal gridlines */}
          {[0.25, 0.5, 0.75].map((t) => (
            <line
              key={t}
              x1={0}
              x2={W}
              y1={PAD_T + (H - PAD_T - PAD_B) * t}
              y2={PAD_T + (H - PAD_T - PAD_B) * t}
              stroke="rgb(226 232 240)"
              strokeDasharray="2 4"
            />
          ))}
          <path d={areaPath} fill="url(#earnGrad)" />
          <path
            d={linePath}
            fill="none"
            stroke="rgb(37 99 235)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: "drop-shadow(0 2px 6px rgba(37,99,235,0.25))" }}
          />
          {/* End-point dot */}
          {points.length > 0 && (
            <>
              <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="4" fill="white" stroke="rgb(37 99 235)" strokeWidth="2" />
              <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="9" fill="rgb(37 99 235)" opacity="0.18">
                <animate attributeName="r" values="6;12;6" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.18;0;0.18" dur="2s" repeatCount="indefinite" />
              </circle>
            </>
          )}
        </svg>
      </div>

      {/* Date axis hint */}
      <div className="mt-1 flex justify-between text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
        <span>{series[0]?.date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

/* -------- goal ring -------- */
function GoalRing({ agent, monthlyGoal = MONTHLY_GOAL_DEFAULT }) {
  const today = new Date();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const day = today.getDate();
  const daysRemaining = daysInMonth - day;

  // Earnings month-to-date (re-use deterministic series)
  const mtdSeries = useMemo(
    () => buildEarningsSeries(agent, day),
    [agent, day]
  );
  const earnedMTD = mtdSeries.reduce((s, p) => s + p.value, 0);
  const dailyRate = earnedMTD / Math.max(1, day);
  const projectedEOM = Math.round(earnedMTD + dailyRate * daysRemaining);

  const pct = Math.min(100, (earnedMTD / monthlyGoal) * 100);
  const projPct = Math.min(100, (projectedEOM / monthlyGoal) * 100);

  // Build SVG ring (270deg arc)
  const R = 56;
  const C = 2 * Math.PI * R;
  const earnedLen = (pct / 100) * C;
  const projLen = (projPct / 100) * C;

  const projWillBeat = projectedEOM >= monthlyGoal;

  return (
    <div
      data-testid="dashboard-goal-ring"
      className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
    >
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-emerald-100 grid place-items-center">
          <Target size={14} className="text-emerald-700" strokeWidth={2.4} />
        </span>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
          Monthly goal · {today.toLocaleString("en-IN", { month: "long" })}
        </p>
      </div>

      <div className="mt-4 flex items-center gap-5">
        <div className="relative w-[136px] h-[136px] shrink-0">
          <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
            {/* Track */}
            <circle cx="70" cy="70" r={R} stroke="rgb(241 245 249)" strokeWidth="12" fill="none" />
            {/* Projection (lighter blue) */}
            <circle
              cx="70"
              cy="70"
              r={R}
              stroke="rgb(96 165 250)"
              strokeWidth="12"
              strokeLinecap="round"
              fill="none"
              strokeDasharray={`${projLen} ${C}`}
              opacity="0.35"
            />
            {/* Actual earned */}
            <circle
              cx="70"
              cy="70"
              r={R}
              stroke="rgb(37 99 235)"
              strokeWidth="12"
              strokeLinecap="round"
              fill="none"
              strokeDasharray={`${earnedLen} ${C}`}
              style={{ transition: "stroke-dasharray 600ms ease" }}
            />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <p className="font-display text-2xl font-black tracking-tighter text-slate-900 tabular-nums leading-none">
                {Math.round(pct)}%
              </p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mt-1">
                of goal
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Earned MTD</p>
          <p className="font-display text-2xl font-black tracking-tighter text-slate-900 tabular-nums leading-none mt-0.5">
            {fmtINR(earnedMTD)}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">of {shortINR(monthlyGoal)} target</p>

          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Projected · EOM
            </p>
            <p className="font-display text-xl font-black tracking-tighter text-slate-900 tabular-nums mt-0.5">
              {fmtINR(projectedEOM)}
            </p>
            <p
              className={`text-[11px] mt-0.5 inline-flex items-center gap-1 font-bold ${
                projWillBeat ? "text-emerald-600" : "text-amber-600"
              }`}
            >
              <TrendingUp size={11} strokeWidth={2.6} />
              {projWillBeat
                ? `Beating goal by ${shortINR(projectedEOM - monthlyGoal)}`
                : `Short by ${shortINR(monthlyGoal - projectedEOM)}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- funnel -------- */
function FunnelWidget({ agent }) {
  const stages = useMemo(() => {
    const seed = (agent?.id || agent?.name || "default") + ":funnel";
    const r = rng(seed);
    // Deterministic funnel — tuned to plausible conversion rates
    const scanned = 1180 + Math.round(r() * 240);
    const proposals = Math.round(scanned * (0.038 + r() * 0.02));
    const deals = Math.round(proposals * (0.18 + r() * 0.08));
    const payouts = Math.round(deals * (0.78 + r() * 0.12));
    return [
      { key: "scan", label: "Briefs scanned", value: scanned, icon: Search, tone: "blue" },
      { key: "pitch", label: "Proposals sent", value: proposals, icon: Send, tone: "sky", conv: (proposals / scanned) * 100 },
      { key: "deal", label: "Deals closed", value: deals, icon: Handshake, tone: "amber", conv: (deals / proposals) * 100 },
      { key: "paid", label: "Payouts cleared", value: payouts, icon: Wallet, tone: "emerald", conv: (payouts / deals) * 100 },
    ];
  }, [agent]);

  const TONE = {
    blue: { bg: "bg-blue-50", iconBg: "bg-blue-100", iconText: "text-blue-700", bar: "bg-blue-500" },
    sky: { bg: "bg-sky-50", iconBg: "bg-sky-100", iconText: "text-sky-700", bar: "bg-sky-500" },
    amber: { bg: "bg-amber-50", iconBg: "bg-amber-100", iconText: "text-amber-700", bar: "bg-amber-500" },
    emerald: { bg: "bg-emerald-50", iconBg: "bg-emerald-100", iconText: "text-emerald-700", bar: "bg-emerald-500" },
  };

  const max = stages[0].value;

  return (
    <div
      data-testid="dashboard-funnel"
      className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
    >
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-blue-100 grid place-items-center">
          <Filter size={13} className="text-blue-700" strokeWidth={2.5} />
        </span>
        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
          Pipeline · this month
        </p>
      </div>

      <div className="mt-4 space-y-3">
        {stages.map((s, i) => {
          const Icon = s.icon;
          const tone = TONE[s.tone];
          const widthPct = (s.value / max) * 100;
          return (
            <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
              {/* Stage row */}
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${tone.iconBg}`}>
                  <Icon size={14} className={tone.iconText} strokeWidth={2.4} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[12px] font-bold text-slate-700">{s.label}</p>
                    <p className="font-display text-lg font-black tracking-tight text-slate-900 tabular-nums">
                      {s.value.toLocaleString("en-IN")}
                    </p>
                  </div>
                  {/* Bar */}
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${tone.bar} rounded-full transition-[width] duration-700 ease-out`}
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </div>
              </div>
              {/* Conversion connector */}
              {i < stages.length - 1 && (
                <div className="pl-11 mt-1.5 mb-0.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 inline-flex items-center gap-1">
                    <span className="block h-3 w-px bg-slate-200" />
                    {stages[i + 1].conv ? `${stages[i + 1].conv.toFixed(1)}% convert` : ""}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------- composed section -------- */
export default function DashboardInsights({ agent }) {
  return (
    <section
      data-testid="dashboard-insights"
      aria-label="Dashboard insights"
      className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5"
    >
      <div className="md:col-span-7">
        <EarningsChart agent={agent} />
      </div>
      <div className="md:col-span-5 grid grid-cols-1 gap-5">
        <GoalRing agent={agent} />
      </div>
      <div className="md:col-span-12">
        <FunnelWidget agent={agent} />
      </div>
    </section>
  );
}
